import React from "react";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
	container: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		height: "100%",
	},
	svg: {
		width: "100%",
		height: "100%",
		maxWidth: 200,
		maxHeight: 200,
	},
	rayGroup: {
		opacity: 0,
		animation: "$rayExplosion 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards",
	},
	"@keyframes rayExplosion": {
		"0%": {
			opacity: 0,
		},
		"15%": {
			opacity: 1,
		},
		"50%": {
			opacity: 1,
		},
		"100%": {
			opacity: 0,
		},
	},
	rayLine: {
		strokeDasharray: "0 1000",
		strokeDashoffset: 0,
		animation: "$rayExpand 0.5s ease-out forwards",
	},
	"@keyframes rayExpand": {
		"0%": {
			strokeDasharray: "0 1000",
			strokeDashoffset: 0,
		},
		"100%": {
			strokeDasharray: "1000 0",
			strokeDashoffset: 0,
		},
	},
	circle: {
		opacity: 0,
		transform: "scale(0)",
		transformOrigin: "center",
		animation: "$circleAppear 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s forwards",
	},
	"@keyframes circleAppear": {
		"0%": {
			opacity: 0,
			transform: "scale(0)",
		},
		"60%": {
			opacity: 1,
			transform: "scale(1.15)",
		},
		"80%": {
			opacity: 1,
			transform: "scale(0.95)",
		},
		"100%": {
			opacity: 1,
			transform: "scale(1)",
		},
	},
	checkmark: {
		opacity: 0,
		strokeDasharray: 100,
		strokeDashoffset: 100,
		animation: "$checkmarkDraw 0.5s ease-out 0.8s forwards",
	},
	"@keyframes checkmarkDraw": {
		"0%": {
			opacity: 0,
			strokeDashoffset: 100,
		},
		"50%": {
			opacity: 1,
		},
		"100%": {
			opacity: 1,
			strokeDashoffset: 0,
		},
	},
}));

const CheckIcon = ({ size = 200 }) => {
	const classes = useStyles();
	
	// Cores baseadas na descrição da imagem
	const greenCircle = "#78E08F"; // Verde vibrante sólido
	const lightGreenRay = "rgba(120, 224, 143, 0.5)"; // Verde claro translúcido para os raios
	const whiteCheck = "#FFFFFF"; // Branco para o checkmark
	
	// Dimensões do círculo central (proporcional ao tamanho)
	const circleRadius = size * 0.3; // 30% do tamanho total
	// Adicionar padding ao SVG para evitar corte dos raios
	const svgPadding = size * 0.1; // 10% de padding em todas as direções
	const centerX = size / 2;
	const centerY = size / 2;
	
	// Configuração dos raios
	const numRays = 12;
	const rayAngleStep = 360 / numRays; // 30 graus entre cada raio
	const rayLength = size * 0.12; // 12% do tamanho total (raios curtos)
	const rayWidth = size * 0.04; // 4% do tamanho total (raios mais largos)
	const rayPadding = size * 0.12; // Padding entre o círculo e os raios (12% do tamanho)
	const rayStartRadius = circleRadius + rayPadding; // Começa após o padding do círculo
	
	// Dimensões do checkmark (proporcional ao círculo)
	const checkmarkSize = circleRadius * 0.7;
	const checkmarkThickness = circleRadius * 0.12; // Checkmark mais fino
	
	// Criar os raios - todos com exatamente o mesmo comprimento
	const rays = [];
	const rayEndRadius = rayStartRadius + rayLength; // Distância final do centro para todos os raios
	
	for (let i = 0; i < numRays; i++) {
		const angle = (i * rayAngleStep - 90) * (Math.PI / 180); // -90 para começar no topo
		const cosAngle = Math.cos(angle);
		const sinAngle = Math.sin(angle);
		
		// Todos os raios começam na mesma distância do centro (rayStartRadius)
		const startX = centerX + cosAngle * rayStartRadius;
		const startY = centerY + sinAngle * rayStartRadius;
		
		// Todos os raios terminam na mesma distância do centro (rayEndRadius)
		// Isso garante que todos tenham exatamente rayLength de comprimento
		const endX = centerX + cosAngle * rayEndRadius;
		const endY = centerY + sinAngle * rayEndRadius;
		
		rays.push({
			x1: startX,
			y1: startY,
			x2: endX,
			y2: endY,
		});
	}
	
	// Coordenadas do checkmark (linha grossa)
	// Checkmark tradicional: linha vertical curta + linha diagonal longa
	const checkmarkLeftX = centerX - checkmarkSize * 0.25;
	const checkmarkLeftY = centerY + checkmarkSize * 0.1;
	const checkmarkMiddleX = centerX - checkmarkSize * 0.05;
	const checkmarkMiddleY = centerY + checkmarkSize * 0.35;
	const checkmarkRightX = centerX + checkmarkSize * 0.35;
	const checkmarkRightY = centerY - checkmarkSize * 0.15;
	
	return (
		<div className={classes.container}>
			<svg
				className={classes.svg}
				width={size}
				height={size}
				viewBox={`-${svgPadding} -${svgPadding} ${size + svgPadding * 2} ${size + svgPadding * 2}`}
				xmlns="http://www.w3.org/2000/svg"
			>
				{/* Raios de luz com animação de explosão */}
				{rays.map((ray, index) => {
					// Delay escalonado muito rápido para efeito de explosão simultânea
					const delay = (index / numRays) * 0.08;
					// Calcular o comprimento do raio para stroke-dasharray
					const dx = ray.x2 - ray.x1;
					const dy = ray.y2 - ray.y1;
					const rayLengthPx = Math.sqrt(dx * dx + dy * dy);
					
					return (
						<g
							key={`ray-group-${index}`}
							className={classes.rayGroup}
							style={{
								animationDelay: `${delay}s`,
							}}
						>
							<line
								x1={ray.x1}
								y1={ray.y1}
								x2={ray.x2}
								y2={ray.y2}
								stroke={lightGreenRay}
								strokeWidth={rayWidth}
								strokeLinecap="round"
								className={classes.rayLine}
								style={{
									animationDelay: `${delay}s`,
									strokeDasharray: `${rayLengthPx} ${rayLengthPx * 3}`,
								}}
							/>
						</g>
					);
				})}
				
				{/* Círculo verde sólido */}
				<circle
					cx={centerX}
					cy={centerY}
					r={circleRadius}
					fill={greenCircle}
					className={classes.circle}
				/>
				
				{/* Checkmark branco grosso */}
				<path
					d={`M ${checkmarkLeftX} ${checkmarkLeftY} L ${checkmarkMiddleX} ${checkmarkMiddleY} L ${checkmarkRightX} ${checkmarkRightY}`}
					stroke={whiteCheck}
					strokeWidth={checkmarkThickness}
					strokeLinecap="round"
					strokeLinejoin="round"
					fill="none"
					className={classes.checkmark}
				/>
			</svg>
		</div>
	);
};

export default CheckIcon;

